/* eslint-disable tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value */
'use client';

import classNames from 'classnames';
import { ApplyCtaLink } from '../../components/ApplyCta';
import Button from '../../components/Button';
import { FeCarouselV2 } from '../../components/FeCarousel';
import { MaterialIcon } from '../../components/MaterialIcon';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getApplyButtonLabel } from '../../utils/helpers/router';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { type Content, formatTierPrice } from './format';
import { CONTENT_TRANSLATIONS, TRANSLATIONS } from './i18n';
import { PlanTierSummaryPanel } from './PlanTierSummaryPanel';
import {
  COMPONENT_TYPE,
  type PlanTierApplyTracking,
  usePlanTierApplyTracking,
} from './tracking';
import type { PlanTierCardData, PlanTierSelectorProps } from './types';

/**
 * M3b Plan Tier Selector (CMS-1724) — the selection hub of the TI Provider
 * Page. Renders the active trip-type tiers as selectable cards (price +
 * discount, an optional recommended badge, an Apply CTA), and reports the
 * tapped tier through `onSelectTier`. On desktop (lg+) a summary panel of the
 * selected tier renders beside the carousel (CMS-1864).
 */
export const PlanTierSelector = ({
  tiers,
  selectedTierId,
  recommendedBadgeLabel,
  onSelectTier,
  isAnnual,
  currencyCode,
  summary,
  pushDataLayer,
  leadingSlot,
}: PlanTierSelectorProps): JSX.Element | null => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  // Card + summary copy, resolved once here and passed down (data flows in as
  // raw values from the wrapper; all copy resolution stays in the block).
  const content = getClientTranslations(CONTENT_TRANSLATIONS, context);
  // Hooks run before the empty-tiers bail-out below.
  const trackApply = usePlanTierApplyTracking(pushDataLayer, currencyCode);

  if (tiers.length === 0) {
    return null;
  }

  return (
    // Two columns from lg up (Figma 21456:8078): the left card/plate and the
    // summary panel are top-aligned (items-start); the panel is sticky so it
    // trails the plate as the user scrolls. Below lg the panel stays in the DOM
    // but is hidden via CSS (`hidden lg:block`) — CSS-gated rather than JS-gated
    // to avoid hydration mismatches.
    <div className="lg:flex lg:items-start lg:gap-spacing/gap/x">
      {/* From lg up the left column is a single card/plate (Figma 21456:8079)
          wrapping the Trust Strip (leadingSlot), heading and tier cards, so the
          whole M3 block reads as one unit beside the summary panel; its padding
          / gap / radius use the same tokens as the Figma plate. Below lg there
          is no plate (Figma 21534:13647 — a plain section), so the tier carousel
          keeps its full page width. `overflow-x-clip` keeps a long Trust Strip
          from forcing horizontal page scroll without touching the carousel's
          own scroll (it clips at the box edge, not a scroll container). */}
      <section
        data-component="plan-tier-selector"
        className="flex min-w-0 flex-col gap-spacing/gap/y overflow-x-clip lg:flex-1 lg:rounded-radius/card lg:border lg:border-borders/default lg:bg-fill/card lg:px-spacing/padding/x lg:py-spacing/padding/y lg:shadow-block-interactive"
      >
        {leadingSlot}
        <div className="flex flex-col gap-spacing/gap/soft-4px">
          <h2 className="font-bold text-[#1a1a1a] text-font-size/title-1 leading-line-height/title-1">
            {staticText.heading}
          </h2>
          <p className="font-medium text-[#6b7280] text-font-size/caption leading-line-height/caption">
            {tr('subheading', staticText, { count: String(tiers.length) })}
          </p>
        </div>

        <PlanTierCarousel
          tiers={tiers}
          selectedTierId={selectedTierId}
          recommendedBadgeLabel={recommendedBadgeLabel}
          onSelectTier={onSelectTier}
          isAnnual={isAnnual}
          currencyCode={currencyCode}
          brandCurrency={context.currencyDisplay}
          content={content}
          trackApply={trackApply}
        />
      </section>

      <div className="hidden lg:block lg:w-[43%] lg:max-w-screen-2sm lg:shrink-0 lg:sticky lg:top-0">
        <PlanTierSummaryPanel
          source={summary}
          isAnnual={isAnnual}
          currencyCode={currencyCode}
          trackApply={trackApply}
        />
      </div>
    </div>
  );
};

type PlanTierCarouselProps = Pick<
  PlanTierSelectorProps,
  | 'tiers'
  | 'selectedTierId'
  | 'recommendedBadgeLabel'
  | 'onSelectTier'
  | 'isAnnual'
  | 'currencyCode'
> & {
  brandCurrency: string | undefined;
  content: Content;
  trackApply: PlanTierApplyTracking;
};

const PlanTierCarousel = ({
  tiers,
  selectedTierId,
  recommendedBadgeLabel,
  onSelectTier,
  isAnnual,
  currencyCode,
  brandCurrency,
  content,
  trackApply,
}: PlanTierCarouselProps): JSX.Element => (
  <FeCarouselV2
    slidesPerView="auto"
    // No spaceBetween: that applies the gap via Swiper JS (absent in SSR →
    // cards flush until hydration). Omitting it uses FeCarouselV2's default
    // CSS inter-slide margin, which renders server-side (stable gap).
    navigation={{
      showOnDesktop: true,
      showOnMobile: true,
      // Only the in-bounds arrow shows (→ at the start, ← once scrolled).
      disabledClassName: '!hidden',
      prevElClassName: '!left-1',
      nextElClassName: '!right-1',
      prevElement: <CarouselArrow direction="prev" />,
      nextElement: <CarouselArrow direction="next" />,
    }}
    // Top padding leaves room for the recommended badge to overhang without
    // being clipped; !h-auto lets Swiper equalise card heights.
    slideClassName="!h-auto pt-[10px]"
  >
    {tiers.map((tier, index) => (
      <PlanTierCard
        key={tier.id}
        tier={tier}
        index={index}
        isSelected={tier.id === selectedTierId}
        recommendedBadgeLabel={recommendedBadgeLabel}
        onSelect={onSelectTier}
        isAnnual={isAnnual}
        currencyCode={currencyCode}
        brandCurrency={brandCurrency}
        content={content}
        trackApply={trackApply}
      />
    ))}
  </FeCarouselV2>
);

// Circular arrow button used as the carousel's prev/next control (Figma
// 21456:8079) — a right arrow signalling more cards; rotated 180° for prev.
const CarouselArrow = ({
  direction,
}: {
  direction: 'prev' | 'next';
}): JSX.Element => (
  <span className="flex size-8 items-center justify-center rounded-full border border-borders/default bg-fill/card shadow-md">
    <MaterialIcon
      icon="arrow_forward"
      px={18}
      className={classNames('text-[#1a1a1a]', {
        'rotate-180': direction === 'prev',
      })}
    />
  </span>
);

type PlanTierCardProps = {
  tier: PlanTierCardData;
  index: number;
  isSelected: boolean;
  recommendedBadgeLabel: string | undefined;
  onSelect: ((id: string) => void) | undefined;
  isAnnual: boolean;
  currencyCode: string;
  brandCurrency: string | undefined;
  content: Content;
  trackApply: PlanTierApplyTracking;
};

const PlanTierCard = ({
  tier,
  index,
  isSelected,
  recommendedBadgeLabel,
  onSelect,
  isAnnual,
  currencyCode,
  brandCurrency,
  content,
  trackApply,
}: PlanTierCardProps): JSX.Element => {
  const showBadge = !!tier.isRecommended && !!recommendedBadgeLabel;
  // Copy + value formatting resolved in-block from raw price data.
  const price = tier.price
    ? formatTierPrice(tier.price, isAnnual, currencyCode, content, brandCurrency)
    : undefined;
  const ctaLabel = getApplyButtonLabel(
    tr('applyNow', content),
    undefined,
    tier.product.call_to_action,
  );

  return (
    <div
      data-component="plan-tier-card"
      data-selected={isSelected || undefined}
      className={classNames(
        'relative flex h-full w-[150px] shrink-0 flex-col gap-spacing/gap/soft-12px rounded-[14px] bg-fill/card px-spacing/padding/x-sm py-spacing/padding/y-sm',
        // Selected mirrors the Figma "selected" card: a heavier dark border vs
        // the default light border. border-2 on both keeps the box size stable.
        isSelected
          ? 'border-2 border-highlights/gray-dark'
          : 'border-2 border-borders/default',
      )}
    >
      {showBadge && (
        <span
          data-component="plan-tier-badge"
          className="mhg-alpha-typography-badge absolute left-1/2 -top-2 -translate-x-1/2 whitespace-nowrap rounded-[12px] bg-campaign/orange-2 px-2 py-[2px] text-white"
        >
          {recommendedBadgeLabel}
        </span>
      )}

      {/* Selection target: tapping the card body selects the tier. The Apply
          CTA is a sibling link (never nested in this button) so the two
          interactions stay distinct and a11y-valid. aria-pressed conveys the
          selected state without the radiogroup arrow-key contract. */}
      <button
        type="button"
        aria-pressed={isSelected}
        onClick={() => onSelect?.(tier.id)}
        className="flex flex-1 flex-col gap-spacing/gap/soft-12px text-left"
      >
        <span className="mhg-alpha-typography-table-cell-strong text-[#1a1a1a]">
          {tier.name}
        </span>
        {tier.description && (
          <span className="min-h-8 font-medium text-[#6b7280] text-font-size/badge leading-line-height/badge">
            {tier.description}
          </span>
        )}

        {/* mt-auto pushes the divider + price block to the bottom of the card
            so the price/discount sit just above the Apply CTA, with the name
            anchored at the top. */}
        <span aria-hidden className="mt-auto h-px w-full bg-[#e5e7eb]" />
        <span className="flex flex-col gap-spacing/gap/soft-4px">
          {/* Skeleton only while the quote is still coming: a tier the settled
              quote has no premium for shows no price at all. */}
          {tier.isPriceLoading && <PriceSkeleton />}
          {!tier.isPriceLoading && price && (
            <>
              {/* Offered price sits on its own line so the card can size to
                  it; the struck-through original + discount label sit on the
                  row below rather than crowding the price on a narrow card. */}
              <span className="flex flex-col gap-spacing/gap/soft-4px">
                <span className="font-semibold text-[#1a1a1a] text-font-size/subheading-1 leading-line-height/subheading-1">
                  {price.offered}
                </span>
                {(price.original || price.discountLabel) && (
                  <span className="font-medium text-[10px] leading-[16px] text-campaign/orange-2">
                    {price.original && (
                      <span
                        data-component="plan-tier-original-price"
                        className="line-through"
                      >
                        {price.original}
                      </span>
                    )}
                    {price.discountLabel && (
                      <span>{` ${price.discountLabel}`}</span>
                    )}
                  </span>
                )}
              </span>
              {price.period && (
                <span className="text-[10px] leading-normal text-[#6b7280]">
                  {price.period}
                </span>
              )}
            </>
          )}
        </span>
      </button>

      {/* ApplyCtaLink resolves the URL from the product and renders through
          FeLink, forwarding the current query string (UTM / trip criteria) and
          opening the apply-v3 modal. It self-hides when no URL resolves. */}
      <ApplyCtaLink
        product={tier.product}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full"
      >
        <Button
          styleType="cta"
          variant="filled"
          size="small"
          className="w-full justify-center rounded-[24px]"
          onClick={() =>
            trackApply({
              product: tier.product,
              index,
              price: tier.price,
              componentType: COMPONENT_TYPE.tierCard,
            })
          }
        >
          <span className="min-w-0 truncate text-sm">{ctaLabel}</span>
        </Button>
      </ApplyCtaLink>
    </div>
  );
};

// Two stacked bars standing in for the price line while the premium resolves,
// matching the Figma "loading" card state. Each line reserves the resolved
// price's line height (offered price, then period) via `1lh` so a loading card
// keeps the same height as a priced one and the layout doesn't shift when the
// premium arrives; the pulse bar sits centered within each line.
const PriceSkeleton = (): JSX.Element => (
  <span
    data-component="plan-tier-price-skeleton"
    aria-hidden
    className="flex flex-col gap-spacing/gap/soft-4px"
  >
    <span className="flex h-[1lh] items-center text-font-size/subheading-1 leading-line-height/subheading-1">
      <span className="h-[18px] w-full animate-pulse rounded-radius/button bg-fill/alternative-3" />
    </span>
    <span className="flex h-[1lh] items-center text-[10px] leading-normal">
      <span className="h-3 w-2/3 animate-pulse rounded-radius/button bg-fill/alternative-3" />
    </span>
  </span>
);
